import Vue from 'vue'
import axios from 'axios'
import * as types from '../mutation-types'
import md5 from 'md5'
import presetDiscountMixin from '../../mixins/preset_discount'

const buildCourseItem = courses => {
  if (courses.length > 0) {
    let courseItems = []
    courses.forEach(c => {
      if (Array.isArray(c.selected)) {
        c.selected.forEach(cs => {
          const tCourseItem = c.course_items.find(ci => cs == ci.item_id)
          if (tCourseItem) courseItems.push(tCourseItem)
        })
      } else {
        const tCourseItem = c.course_items.find(ci => c.selected == ci.item_id)
        if (tCourseItem) courseItems.push(tCourseItem)
      }
    })
    return courseItems.map(ci => {
      return {
        courseItemId: ci && ci.course_item_id ? ci.course_item_id : 0,
        selectedCourseItems: ci && ci.has_courses == 1 ? buildCourseItem(ci.courses) : []
      }
    })
  }
  return []
}

// namespaced
export const namespaced = true

// state
export const state = {
  msg: null,
  activeOrder: null,
  activeBill: null,
  orders: [],
  queues: [],

  pendingOrders: [],
  pastOrders: [],

  useVouchers: true,
  usePoints: false,
  useCredit: false,
  paymentMode: null,

  transid: null,
  openTables: null
}

// getters
export const getters = {
  orderMsg: state => state.msg,
  activeOrder: state => state.activeOrder,
  activeBill: state => state.activeBill,
  orders: state => state.orders,
  queues: state => state.queues,

  pendingOrders: state => state.pendingOrders,
  pastOrders: state => state.pastOrders,

  usePoints: state => state.usePoints,
  useCredit: state => state.useCredit,
  paymentMode: state => state.paymentMode,
  transid: state => state.transid,
  openTables: state => state.openTables,

  redeemed: state => {
    if (!state.orders || state.orders.length === 0) return 0
    return state.orders
      .filter(o => o.payments_points !== null)
      .map(o => parseFloat(o.payments_points))
      .reduce((a, b) => a + b)
  },

  activeOrderPayments: (state, getters, rootState) => {
    const paymentAmount = parseFloat((Math.round(((state.activeOrder ? parseFloat(state.activeOrder.payable_amount) : 0) + (state.activeOrder ? parseFloat(state.activeOrder.payments_discount) : 0)) * 100) / 100).toFixed(2))
    let paymentPoints
    if (!state.usePoints || !rootState.member.points) {
      paymentPoints = 0
    } else {
      const points = parseFloat(rootState.member.points)
      if (points === 0) {
        paymentPoints = 0
      } else if (points < paymentAmount) {
        paymentPoints = points
      } else {
        paymentPoints = paymentAmount
      }
    }
    return {
      points: paymentPoints,
      credits: 0,
      topay_payment: parseFloat(Math.round((paymentAmount - paymentPoints) * 100) / 100).toFixed(2)
    }
  }
}

// mutations
export const mutations = {
  [types.RESET_ORDER_DATA] (state) {
    Object.assign(state, {
      msg: null,
      activeOrder: null,
      activeBill: null,
      orders: [],
      queues: [],

      pendingOrders: [],
      pastOrders: [],

      usePoints: false,
      useCredit: false,
      paymentMode: null,

      transid: null,
      openTables: null
    })
  },

  [types.RESET_ACTIVE_ORDER_DATA] (state) {
    state.msg = null
    state.activeOrder = null
    state.activeBill = null
    state.usePoints = false
    state.useCredit = false
    state.paymentMode = null
    state.transid = null
    state.openTables = null
  },

  [types.SET_ORDER_MSG] (state, msg) {
    const msgs = {
      order: 'Failed to fetch order data',
      order2: 'Failed to save order data',
      checkout: 'Failed to checkout',
      confirm: 'Failed to confirm order',
      queue: 'Could not get queue number. You may try later.',
      transaction: 'Could not add transaction.',
      points: 'Failed to get order points',
      bills: 'Failed to fetch bills',
      bill: 'Failed to fetch bill data',
      bill2: 'Failed to save bill data',
      email2: 'email_receipt_fail_notice',
      calculate: 'Failed to calculate order'
    }
    const message = msg ? (msgs[msg] ? msgs[msg] : msg) : 'Failed to fetch data'
    state.msg = message
  },

  [types.RESET_ORDER_MSG] (state) {
    state.msg = null
  },

  [types.FETCH_ORDERS] (state, { orders, time }) {
    state.orders = orders
    state.pendingOrders = orders.filter(o => {
      if (o.pickup_time) {
        if (o.pickup_time >= time) return o
      } else {
        if (o.created_at >= time) return o
      }
    })
    state.pastOrders = orders.filter(o => {
      if (o.pickup_time) {
        if (o.pickup_time < time) return o
      } else {
        if (o.created_at < time) return o
      }
    })
    state.queues = state.pendingOrders.filter(o => o.queue_no).map(o => o.queue_no)
  },

  [types.CHECK_ORDER] (state, payload) {
    const amount = payload.total.replace(/[^\d.-]/g, '')
    const discount = payload.discount_total.replace(/[^\d.-]/g, '')
    const topay = payload.payable.replace(/[^\d.-]/g, '')
    const points = parseFloat(Math.round((parseFloat(amount) + parseFloat(discount) - parseFloat(topay)) * 100) / 100)
    delete payload.code
    state.activeOrder = Object.assign({}, payload, {
      payable_amount: amount,
      payments_discount: discount,
      payments_topay: topay,
      payments_points: points,
      payments_credit: 0
    })
  },

  [types.CHECK_SUBMIT_ORDER] (state, payload) {
    // payload is reference_id and items
    state.activeOrder = payload
  },

  [types.CHECK_FINAL_ORDER] (state, payload) {
    const amount = payload.total.replace(/[^\d.-]/g, '')
    const discount = payload.discount_total.replace(/[^\d.-]/g, '')
    const topay = payload.payable.replace(/[^\d.-]/g, '')
    const points = parseFloat(Math.round((parseFloat(amount) + parseFloat(discount) - parseFloat(topay)) * 100) / 100)
    delete payload.code
    state.activeOrder = Object.assign({}, state.activeOrder, payload, {
      payable_amount: amount,
      payments_discount: discount,
      payments_topay: topay,
      payments_points: points,
      payments_credit: 0
    })
  },

  [types.ADD_ORDER] (state, payload) {
    // payload is order
    const i = state.orders.findIndex(o => o.order_type === payload.order_type && parseInt(o.id) === parseInt(payload.id))
    if (i === -1) { // only add new order
      state.orders.unshift(payload)
    }
  },

  [types.UPDATE_ORDER] (state, payload) {
    // payload is order
    const i = state.orders.findIndex(o => o.order_type === payload.order_type && parseInt(o.id) === parseInt(payload.id))
    if (i !== -1) {
      Vue.set(state.orders, i, payload)
    }
  },

  [types.UPDATE_ACTIVE_ORDER] (state, payload) {
    // payload is order
    state.activeOrder = payload
  },

  [types.RESET_ORDER] (state) {
    state.activeOrder = null
  },

  [types.CONFIRM_ORDER] (state, payload) {
    // payload is order
    const i = state.orders.findIndex(o => parseInt(o.reference_id) === parseInt(payload.reference_id))
    if (i !== -1) {
      Vue.set(state.orders, i, payload)
    }
  },

  [types.CANCEL_ORDER] (state, payload) {
    // payload is reference_id
    state.orders = state.orders.filter(function (order) {
      return order.reference_id !== payload
    })
  },

  [types.SAVE_POINTS] (state, payload) {
    // payload is order
    const i = state.orders.findIndex(o => parseInt(o.reference_id) === parseInt(payload.reference_id))
    if (i !== -1) {
      Vue.set(state.orders, i, payload)
    }
  },

  [types.SAVE_QUEUE] (state, payload) {
    // payload is order
    const i = state.orders.findIndex(o => parseInt(o.reference_id) === parseInt(payload.reference_id))
    if (i !== -1) {
      Vue.set(state.orders, i, payload)
    }
  },

  [types.SET_QUEUES] (state, payload) {
    // payload is array of number-strings
    state.queues = payload
  },

  [types.RESET_QUEUES] (state) {
    state.queues = []
  },

  [types.SET_PAYMENT_MODE] (state, payload) {
    state.paymentMode = payload
  },

  [types.RESET_PAYMENT_MODE] (state) {
    state.paymentMode = null
  },

  [types.ENABLE_USE_POINTS] (state) {
    state.usePoints = true
  },

  [types.DISABLE_USE_POINTS] (state) {
    state.usePoints = false
  },

  [types.ENABLE_USE_VOUCHERS] (state) {
    state.useVouchers = true
  },

  [types.DISABLE_USE_VOUCHERS] (state) {
    state.useVouchers = false
  },

  [types.SET_TRANSID] (state, payload) {
    // payload is browserId
    const d = new Date()
    const p1 = ('00' + Math.floor(Math.random() * 100)).slice(-3)
    const p2 = ('00' + d.getMilliseconds()).slice(-3)
    const padded = p1 + p2
    state.transid = payload + '' + padded
  },

  [types.RESET_TRANSID] (state) {
    state.transid = null
  },

  [types.SET_OPEN_TABLES] (state, payload) {
    state.openTables = payload
  },

  [types.RESET_OPEN_TABLES] (state) {
    state.openTables = []
  },

  [types.SET_BILL] (state, payload) {
    const i = state.openTables.findIndex(t => parseInt(t.table_id) === parseInt(payload.tid))
    if (i !== -1) {
      state.activeBill = payload
      const openTable = Object.assign({}, state.openTables[i], {
        bill: { ...payload }
      })
      Vue.set(state.openTables, i, openTable)
    }
  },

  [types.UPDATE_ACTIVE_BILL] (state, payload) {
    state.activeBill = payload
  },

  [types.RESET_BILL] (state) {
    state.activeBill = null
  }
}

// actions
export const actions = {
  resetOrderData ({ commit }) {
    commit(types.RESET_ORDER_DATA)
  },

  resetActiveOrderData ({ commit }) {
    commit(types.RESET_ACTIVE_ORDER_DATA)
  },

  async fetchAllOrders ({ commit, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/orders/all', {
        loyalty_group_id: parseInt(rootGetters['config/loyaltyGroupId']),
        restaurant_id: parseInt(rootGetters['config/rid']) === 0 ? parseInt(rootGetters['config/rid2']) : parseInt(rootGetters['config/rid'])
      })
      // data is { orders, unpaid_orders, time }
      const orders = (data.orders.map(o => ({ ...o, order_type: 'order' }))).concat(data.unpaid_orders.map(o => ({ ...o, order_type: 'unpaid_order' })))
      commit(types.FETCH_ORDERS, {
        orders: orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        time: data.time
      })
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order')
    }
  },

  async fetchAllOrders2 ({ commit, state }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.get('/api/date/queue')
      commit(types.FETCH_ORDERS, {
        orders: state.orders,
        time: data
      })
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order')
    }
  },

  // First group_order (with status 0) - For Old POS
  async checkOrder ({ commit, state, rootState, rootGetters }, payload) {
    // payload is payments breakdown { points, credits, topay_payment }
    commit(types.RESET_ORDER_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      const modeData = rootState.config.mode
      const items = rootGetters['cart/cart'].map(item => {
        const tItem = Object.assign({}, item, {
          discount_id: item.preset_discount_id,
          options: [],
          toppings: []
        })

        const presetDiscount = presetDiscountMixin.methods.calculateAvailablePresetDiscount(item, rootGetters['auth/user'], item.quantity)
        console.log('presetDiscount 401')
        console.log(presetDiscount)
        console.log('presetDiscount')
        if (presetDiscount) {
          tItem.PresetDiscountID = presetDiscount.ID
        }

        console.log('--------item--------')
        console.log(tItem)
        console.log('PresetDiscountID: ' + tItem.PresetDiscountID)
        console.log('--------item--------')

        if (item.price_override) {
          tItem.priceow = item.price_override
          tItem.itemReference = md5('aptsys price' + item.price + 'priceow' + item.price_override + 'quantity' + item.quantity + 'service_charge' + item.service_charge + 'tax' + item.tax)
        } else {
          tItem.itemReference = md5('aptsys price' + item.price + 'quantity' + item.quantity + 'service_charge' + item.service_charge + 'tax' + item.tax)
        }

        return _.omit(tItem, ['cart_id', 'photo_url', 'max_quantity', 'min_quantity', 'is_promo', 'preset_discounts'])
      })
      let paymentModeData = null
      if (state.paymentMode) {
        paymentModeData = state.paymentMode
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      let memberId, memberName, memberMobile
      console.log('Initial mobileMobile variable. 415')
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        memberName = user.name
        if (/^$|^[6|8|9|3].{7}$/.test(user.mobile)) {
          memberMobile = '+65 ' + user.mobile
        } else {
          memberMobile = '+' + user.country_code + ' ' + user.mobile
        }
        console.log('memberMobile = \'+\' + user.country_code + \' \' + user.mobile', memberMobile)
        // if order for someone else
        if (modeData.isDelivery && rootGetters['delivery/address'].contact_name && rootGetters['delivery/address'].contact_name !== '') {
          memberName = rootGetters['delivery/address'].contact_name
        }
        if (modeData.isDelivery && rootGetters['delivery/address'].contact_no && rootGetters['delivery/address'].contact_no !== '') {
          // memberMobile = '+' + user.country_code + ' ' + rootGetters['delivery/address'].contact_no

          // The phone number of the delivery address can only be entered as +65
          memberMobile = '+65 ' + rootGetters['delivery/address'].contact_no
          console.log('memberMobile = \'+65\' + \' \' + rootGetters[\'delivery/address\'].contact_no', memberMobile)
        }
      } else {
        memberId = rootGetters['config/guestId']
        memberName = rootGetters['config/guestName']
        memberMobile = ''
        console.log('memberMobile = \'\'', memberMobile)
      }
      let deliveryCharge = 0
      let tableName, delivery, takeAwayAt, totTimeRequired
      if (modeData.isTakeaway) {
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableName = taTable ? taTable.qr_code : ''
        delivery = null
        takeAwayAt = timeData.data
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else if (modeData.isDelivery) {
        const deliveryPlu = rootGetters['delivery/deliveryInfo'].delivery_plu
        if (deliveryPlu) {
          items.push({
            item_id: deliveryPlu.item_id,
            tax: deliveryPlu.tax,
            service_charge: deliveryPlu.service_charge,
            plu_id: deliveryPlu.plu_id,
            price: deliveryPlu.plu_price,
            priceow: deliveryPlu.plu_price,
            quantity: 1,
            remarks: '',
            non_discount: deliveryPlu.non_discount,
            name: deliveryPlu.item_name
          })
          deliveryCharge = deliveryPlu.plu_price
        }
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableName = taTable ? taTable.qr_code : ''
        delivery = rootGetters['delivery/address']
        takeAwayAt = timeData.data
        totTimeRequired = rootGetters['cart/cartTimeRequired']
        memberMobile += ' [' + rootGetters['delivery/addressString'] + ']'
        console.log('memberMobile += \' [\' + rootGetters[\'delivery/addressString\'] + \']\'', memberMobile)
      } else {
        tableName = rootGetters['config/tb']
        delivery = null
        takeAwayAt = null
        totTimeRequired = 0
      }
      const paymentTypeId = paymentModeData ? paymentModeData.type : null
      const restaurant = rootGetters['config/restaurant']
      // Select whether to apply cart discount, or dine mode's preset discount
      let discountInfo = {
        discount_id: '',
        discount_amount: '',
        discount_type: '',
        non_discount: ''
      }
      if (rootGetters['cart/discountInfoIsFilled'] || modeData.hasPresetDiscount) {
        const cartDiscountInfo = rootGetters['cart/discountInfo']
        discountInfo = parseFloat(cartDiscountInfo.discount_amount || 0) > parseFloat(modeData.presetDiscount.discount_amount || 0)
        ? cartDiscountInfo
        : modeData.presetDiscount
      }
      console.log('/api/check items: ')
      console.log(JSON.parse(JSON.stringify(items)))
      const order = {
        ...restaurant,
        payment_type_id: paymentTypeId,
        table_name: tableName,
        items: items,
        ...discountInfo,
        tot_time_required: totTimeRequired,
        delivery: delivery
      }
      const info = {
        device: rootGetters['config/posType'] === 0 ? rootGetters['config/device'] : 'jade',
        with_table: rootGetters['config/withTableNo'],
        is_take_away: (modeData.isTakeaway || modeData.isDelivery) ? 1 : 0,
        takeaway_at: takeAwayAt, // yyyy-mm-dd hh:mm:ss
        member_id: memberId,
        member_name: memberName,
        member_mobile: memberMobile,
        is_paid: 1,
        pay_later: 0,
        save_to_db: 0
      }
      const params = {
        info: info,
        order: order,
        vouchers: rootGetters['cart/vouchers'],
        payments: payload,
        delivery_charge: deliveryCharge
      }
      console.log('/api/check params:')
      console.log(JSON.parse(JSON.stringify(params)))
      const { data } = await axios.post('/api/check', params)
      if (data.code === 1) {
        const payload = { ...data.order, items: rootGetters['cart/cart'] }
        commit(types.CHECK_ORDER, payload)
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'checkout')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'checkout')
    }
  },

  // First group_order (with status 1) - For Old POS
  async checkFinalOrder ({ commit, state, rootState, rootGetters }, payload) {
    // payload is payments breakdown { points, credits, topay_payment }
    commit(types.RESET_ORDER_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      const modeData = rootState.config.mode
      const items = rootGetters['cart/cart'].map(item => {
        const tItem = Object.assign({}, item, {
          discount_id: item.preset_discount_id,
          options: [],
          toppings: []
        })

        const presetDiscount = presetDiscountMixin.methods.calculateAvailablePresetDiscount(item, rootGetters['auth/user'], item.quantity)

        if (presetDiscount) {
          tItem.PresetDiscountID = presetDiscount.ID
        }

        if (item.price_override) {
          tItem.priceow = item.price_override
          tItem.itemReference = md5('aptsys price' + item.price + 'priceow' + item.price_override + 'quantity' + item.quantity + 'service_charge' + item.service_charge + 'tax' + item.tax)
        } else {
          tItem.itemReference = md5('aptsys price' + item.price + 'quantity' + item.quantity + 'service_charge' + item.service_charge + 'tax' + item.tax)
        }
        return _.omit(tItem, ['cart_id', 'photo_url', 'max_quantity', 'min_quantity', 'is_promo'])
      })
      let paymentModeData = null
      if (state.paymentMode) {
        paymentModeData = state.paymentMode
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      let memberId, memberName, memberMobile
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        memberName = user.name
        if (/^$|^[6|8|9|3].{7}$/.test(user.mobile)) {
          memberMobile = '+65' + ' ' + user.mobile
        } else {
          memberMobile = '+' + user.country_code + ' ' + user.mobile
        }
        // if order for someone else
        if (modeData.isDelivery && rootGetters['delivery/address'].contact_name && rootGetters['delivery/address'].contact_name !== '') {
          memberName = rootGetters['delivery/address'].contact_name
        }
        if (modeData.isDelivery && rootGetters['delivery/address'].contact_no && rootGetters['delivery/address'].contact_no !== '') {
          // memberMobile = '+' + user.country_code + ' ' + rootGetters['delivery/address'].contact_no

          // The phone number of the delivery address can only be entered as +65
          memberMobile = '+65 ' + rootGetters['delivery/address'].contact_no
        }
      } else {
        memberId = rootGetters['config/guestId']
        memberName = rootGetters['cart/guestDetails'].name ? rootGetters['cart/guestDetails'].name : rootGetters['config/guestName']
        memberMobile = rootGetters['cart/guestDetails'].country_code && rootGetters['cart/guestDetails'].mobile ? ('+' + rootGetters['cart/guestDetails'].country_code + ' ' + rootGetters['cart/guestDetails'].mobile) : ''
      }
      let deliveryCharge = 0
      let tableName, delivery, takeAwayAt, totTimeRequired
      if (modeData.isTakeaway) {
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableName = taTable ? taTable.qr_code : ''
        delivery = null
        takeAwayAt = timeData.data
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else if (modeData.isDelivery) {
        const deliveryPlu = rootGetters['delivery/deliveryInfo'].delivery_plu
        if (deliveryPlu) {
          items.push({
            item_id: deliveryPlu.item_id,
            tax: deliveryPlu.tax,
            service_charge: deliveryPlu.service_charge,
            plu_id: deliveryPlu.plu_id,
            price: deliveryPlu.plu_price,
            priceow: deliveryPlu.plu_price,
            quantity: 1,
            remarks: '',
            non_discount: deliveryPlu.non_discount,
            name: deliveryPlu.item_name
          })
          deliveryCharge = deliveryPlu.plu_price
        }
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableName = taTable ? taTable.qr_code : ''
        delivery = rootGetters['delivery/address']
        takeAwayAt = timeData.data
        totTimeRequired = rootGetters['cart/cartTimeRequired']
        memberMobile += ' [' + rootGetters['delivery/addressString'] + ']'
      } else {
        tableName = rootGetters['config/tb']
        delivery = null
        takeAwayAt = null
        totTimeRequired = 0
      }
      const paymentTypeId = paymentModeData ? paymentModeData.type : null
      const restaurant = rootGetters['config/restaurant']
      // Select whether to apply cart discount, or dine mode's preset discount
      let discountInfo = {
        discount_id: '',
        discount_amount: '',
        discount_type: '',
        non_discount: ''
      }
      // Override the discount info
      if (rootGetters['cart/discountInfoIsFilled'] || modeData.hasPresetDiscount) {
        const cartDiscountInfo = rootGetters['cart/discountInfo']
        discountInfo = parseFloat(cartDiscountInfo.discount_amount || 0) > parseFloat(modeData.presetDiscount.discount_amount || 0)
        ? cartDiscountInfo
        : modeData.presetDiscount
      }
      const order = {
        ...restaurant,
        payment_type_id: paymentTypeId,
        table_name: tableName,
        items: items,
        ...discountInfo,
        tot_time_required: totTimeRequired,
        delivery: delivery
      }
      const info = {
        device: rootGetters['config/posType'] === 0 ? rootGetters['config/device'] : 'jade',
        with_table: rootGetters['config/withTableNo'],
        is_take_away: (modeData.isTakeaway || modeData.isDelivery) ? 1 : 0,
        takeaway_at: takeAwayAt, // yyyy-mm-dd hh:mm:ss
        member_id: memberId,
        member_name: memberName,
        member_mobile: memberMobile,
        is_paid: 1,
        pay_later: 0
      }
      const params = {
        info: info,
        order: order,
        vouchers: rootGetters['cart/vouchers'],
        payments: payload,
        delivery_charge: deliveryCharge
      }
      const { data } = await axios.post('/api/check', params)
      if (data.code === 1) {
        commit(types.CHECK_FINAL_ORDER, { ...data.order, reference_type: rootGetters['config/posType'] })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'checkout')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'checkout')
    }
  },

  // First mobile_order (with status 1) - For Jade POS
  async checkFinalOrder2 ({ commit, state, rootState, rootGetters }, payload) {
    // payload is payments breakdown { points, credits, topay_payment }
    commit(types.RESET_ORDER_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      const modeData = rootState.config.mode
      const plus = rootGetters['cart/cart'].map(p => {
        const item = {
          id: p.plu_id,
          quantity: Math.abs(p.quantity),
          remarks: p.remarks,
          selectedCourseItems: buildCourseItem(p.courses)
        }
        if (p.price_override) item.price_overwrite = p.price_override
        return item
      })
      const guest = !rootGetters['member/isMember']
        ? {
          id: rootGetters['config/guestId'],
          name: rootGetters['cart/guestDetails'].name ? rootGetters['cart/guestDetails'].name : rootGetters['config/guestName'],
          country_code: rootGetters['cart/guestDetails'].country_code ? rootGetters['cart/guestDetails'].country_code : '',
          mobile: rootGetters['cart/guestDetails'].mobile ? rootGetters['cart/guestDetails'].mobile : '',
          email: rootGetters['cart/guestDetails'].email ? rootGetters['cart/guestDetails'].email : ''
        }
        : null
      let takeAway, delivery, takeAwayAt, tableId, tableName, totTimeRequired
      if (modeData.isTakeaway) {
        takeAway = 1
        delivery = null
        takeAwayAt = timeData.data
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableId = taTable ? taTable.ID : 0
        tableName = taTable ? taTable.Name : ''
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else if (modeData.isDelivery) {
        const deliveryPlu = rootGetters['delivery/deliveryInfo'].delivery_plu
        if (deliveryPlu) {
          plus.push({
            id: deliveryPlu.plu_id,
            quantity: 1,
            price_overwrite: deliveryPlu.plu_price,
            remarks: '',
            name: deliveryPlu.item_name
          })
        }
        takeAway = 1
        delivery = rootGetters['delivery/address']
        takeAwayAt = timeData.data
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableId = taTable ? taTable.ID : 0
        tableName = taTable ? taTable.Name : ''
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else {
        if (!rootGetters['config/withTableNo']) {
          const table = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 0) : null
          tableId = table ? table.ID : ''
          tableName = table ? table.Name : ''
        } else {
          tableId = rootGetters['config/tbdata'].ID
          tableName = rootGetters['config/tbdata'].Name
        }
        takeAway = 0
        delivery = null
        takeAwayAt = null
        totTimeRequired = 0
      }
      // Select whether to apply cart discount, or dine mode's preset discount
      let discountId = 0
      if (rootGetters['cart/discountInfoIsFilled'] || modeData.hasPresetDiscount) {
        const cartDiscountInfo = rootGetters['cart/discountInfo']
        discountId = parseFloat(cartDiscountInfo.discount_amount || 0) > parseFloat(modeData.presetDiscount.discount_amount || 0)
        ? parseInt(cartDiscountInfo.discount_id)
        : parseInt(modeData.presetDiscount.discount_id)
      }
      let paymentModeData = null
      if (state.paymentMode) {
        paymentModeData = state.paymentMode
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      const paymentTypeId = paymentModeData ? paymentModeData.type : null
      const params = {
        group_id: parseInt(rootGetters['config/gid']),
        restaurant_id: parseInt(rootGetters['config/rid']),
        member_id: rootGetters['member/isMember'] ? rootGetters['member/profile'].loyalty_member_id : 0,
        guest: guest,
        takeaway: takeAway,
        delivery: delivery,
        takeaway_at: takeAwayAt,
        table: {
          id: tableId,
          name: tableName
        },
        plus: plus,
        discount_id: discountId,
        vouchers: rootGetters['cart/vouchers'],
        tot_time_required: totTimeRequired,
        payment: {
          ...payload,
          payment_type_id: paymentTypeId
        }
      }
      const { data } = await axios.post('/api/check2', params)
      if (data.code === 1) {
        commit(types.CHECK_FINAL_ORDER, {
          reference_type: rootGetters['config/posType'],
          reference_id: data.reference_id,
          total: state.activeOrder.total,
          discount_total: state.activeOrder.discount_total,
          payable: state.activeOrder.payable
        })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'checkout')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'checkout')
    }
  },

  // Insert into local orders table
  async addOrder ({ commit, state, rootState, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      let memberId, userId
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        userId = user.id
      } else {
        memberId = rootGetters['config/guestId']
        userId = 0
      }
      let paymentModeData = null
      if (state.paymentMode) {
        paymentModeData = state.paymentMode
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      const paymentType = paymentModeData ? paymentModeData.code : null
      const modeData = rootState.config.mode
      const params = {
        user_id: userId,
        member_id: memberId,
        reference_type: rootGetters['config/posType'],
        restaurant_id: parseInt(rootGetters['config/rid']),
        payment_type: paymentType,
        pickup_time: (modeData.isTakeaway || modeData.isDelivery) ? timeData.data : null,
        location: rootGetters['config/config'].location,
        tablename: modeData.isDinein && rootGetters['config/withTableNo'] ? rootGetters['config/tbdata'].Name : null,
        address: modeData.isDelivery ? rootGetters['delivery/addressString'] : null,
        is_refunded: 0
      }
      const order = Object.assign({}, state.activeOrder, params)
      const { data } = await axios.post('/api/orders/add', {
        loyalty_group_id: parseInt(rootGetters['config/loyaltyGroupId']),
        order: order
      })
      if (data) {
        const order2 = data
        order2.order_type = 'order'
        order2.trays = order2.trays ? JSON.parse(order2.trays) : []
        order2.items = JSON.parse(order2.items)
        if (order2.vouchers) {
          order2.vouchers = JSON.parse(order2.vouchers)
        }
        commit(types.UPDATE_ACTIVE_ORDER, order2)
        commit(types.ADD_ORDER, order2)
      } else {
        commit(types.SET_ORDER_MSG, 'order2')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order2')
    }
  },

  // Insert into local unpaid_orders table
  async addUnpaidOrder ({ commit, state, rootState, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      let memberId, userId
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        userId = user.id
      } else {
        memberId = rootGetters['config/guestId']
        userId = 0
      }
      const modeData = rootState.config.mode
      const params = {
        user_id: userId,
        member_id: memberId,
        restaurant_id: parseInt(rootGetters['config/rid']),
        pickup_time: (modeData.isTakeaway || modeData.isDelivery) ? timeData.data : null,
        location: rootGetters['config/config'].location,
        tablename: modeData.isDinein && rootGetters['config/withTableNo'] ? rootGetters['config/tbdata'].Name : null,
        address: modeData.isDelivery ? rootGetters['delivery/addressString'] : null
      }

      let activeOrder = state.activeOrder

      if (Array.isArray(activeOrder.items)) {
        activeOrder.items = activeOrder.items
          .map(item => _.omit(item, ['preset_discounts']))
      }

      const order = Object.assign({}, activeOrder, params)
      const { data } = await axios.post('/api/unpaidorders/add', {
        loyalty_group_id: parseInt(rootGetters['config/loyaltyGroupId']),
        order: order
      })
      if (data) {
        const order2 = data
        order2.order_type = 'unpaid_order'
        order2.items = JSON.parse(order2.items)
        if (order2.vouchers) {
          order2.vouchers = JSON.parse(order2.vouchers)
        }
        commit(types.UPDATE_ACTIVE_ORDER, order2)
        commit(types.ADD_ORDER, order2)
      } else {
        commit(types.SET_ORDER_MSG, 'order2')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order2')
    }
  },

  // First group_order (with status 1) - points payment
  async confirmActiveOrder ({ commit, state }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const params = {
        reference_id: state.activeOrder.reference_id
      }
      const { data } = await axios.post('/api/confirm', params)
      if (data.code === 1) {
        commit(types.CONFIRM_ORDER, { ...data.order, order_type: 'order' })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'confirm')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'confirm')
    }
  },

  // First group_order (with status 1) - points payment
  async confirmActiveOrder2 ({ commit, state }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const params = {
        mobile_order_id: state.activeOrder.reference_id,
        payment_details: []
      }
      const { data } = await axios.post('/api/confirm2', params)
      if (data.code === 1) {
        commit(types.CONFIRM_ORDER, { ...data.order, order_type: 'order' })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'confirm')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'confirm')
    }
  },

  // Ask Jade to calculate price
  async calculateOrder ({ commit, rootState, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const modeData = rootState.config.mode
      const plus = rootGetters['cart/cart'].map(p => {
        const item = {
          id: p.plu_id,
          quantity: Math.abs(p.quantity),
          selectedCourseItems: buildCourseItem(p.courses)
        }
        if (p.price_override) item.price_overwrite = p.price_override
        return item
      })
      let deliveryCharge = 0
      if (modeData.isDelivery) {
        const deliveryPlu = rootGetters['delivery/deliveryInfo'].delivery_plu
        if (deliveryPlu) {
          plus.push({
            id: deliveryPlu.plu_id,
            quantity: 1,
            price_overwrite: deliveryPlu.plu_price,
            name: deliveryPlu.item_name
          })
          deliveryCharge = deliveryPlu.plu_price
        }
      }
      // Select whether to apply cart discount, or dine mode's preset discount
      let discountId = 0
      if (rootGetters['cart/discountInfoIsFilled'] || modeData.hasPresetDiscount) {
        const cartDiscountInfo = rootGetters['cart/discountInfo']
        discountId = parseFloat(cartDiscountInfo.discount_amount || 0) > parseFloat(modeData.presetDiscount.discount_amount || 0)
        ? parseInt(cartDiscountInfo.discount_id)
        : parseInt(modeData.presetDiscount.discount_id)
      }
      const params = {
        group_id: parseInt(rootGetters['config/gid']),
        restaurant_id: parseInt(rootGetters['config/rid']),
        takeaway: (modeData.isTakeaway || modeData.isDelivery) ? 1 : 0, // so that server won't charge SVC
        delivery_charge: deliveryCharge,
        plus: plus,
        discount_id: discountId
      }
      const { data } = await axios.post('/api/calculate', params)
      if (data.code === 1) {
        const payload = { ...data.order, items: rootGetters['cart/cart'] }
        commit(types.CHECK_ORDER, payload)
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'calculate')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'calculate')
    }
  },

  // First mobile_order (with status 1) - For Old POS
  async checkSubmitOrder ({ commit, state, rootState, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const addInCourseItem = []
      const currentTimeData = await axios.get('/api/date')
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      const modeData = rootState.config.mode
      rootGetters['cart/cart'].filter(item => (item.set_course_items).length > 0)
      .forEach(item => {
        const tItem = Object.assign({}, item, {
          isItemSetMeal: 1,
          hiddenModifers: [],
          qty: Math.abs(item.quantity),
          uniqueID: item.item_id,
          PresetDiscountID: item.PresetDiscountID ? item.PresetDiscountID : 0,
          pluID: item.plu_id,
          setMealID: 0,
          rmks: item.remarks,
          courseLevel: 0
        })

        const presetDiscount = presetDiscountMixin.methods.calculateAvailablePresetDiscount(item, rootGetters['auth/user'], item.quantity)

        if (presetDiscount) {
          tItem.PresetDiscountID = presetDiscount.ID
        }

        if (item.price_override) tItem.priceow = item.price_override
        if (item.member_voucher_id) tItem.voucherID2 = item.member_voucher_id
        addInCourseItem.push(_.omit(tItem, ['cart_id', 'voucher_id', 'member_voucher_id',
          'item_id', 'category_id', 'plu_id', 'quantity', 'final_price', 'tax', 'service_charge',
          'remarks', 'options', 'toppings', 'is_set_meal', 'set_course_items', 'photo_url',
          'courses', 'option_groups', 'max_quantity', 'min_quantity', 'is_promo', 'preset_discounts']))
        item.set_course_items.forEach(i => {
          i.qty = Math.abs(item.quantity)
          addInCourseItem.push(i)
        })
      })

      const items = rootGetters['cart/cart']
        .filter(item => (item.set_course_items).length === 0)
        .map(item => {
          const presetDiscount = presetDiscountMixin.methods.calculateAvailablePresetDiscount(item, rootGetters['auth/user'], item.quantity)
          if (presetDiscount) {
            item.PresetDiscountID = presetDiscount.ID
          }

          item.hiddenModifers = []
          item.qty = Math.abs(item.quantity)
          item.uniqueID = item.item_id
          item.PresetDiscountID = item.PresetDiscountID ? item.PresetDiscountID : 0
          item.pluID = item.plu_id
          item.setMealID = 0
          item.rmks = item.remarks
          item.courseLevel = 0
          if (item.member_voucher_id) item.voucherID2 = item.member_voucher_id
          if (item.price_override) item.priceow = item.price_override
          return _.omit(item, ['cart_id', 'voucher_id', 'member_voucher_id',
            'item_id', 'category_id', 'plu_id', 'quantity', 'final_price', 'tax',
            'service_charge', 'remarks', 'options', 'toppings', 'is_set_meal',
            'set_course_items', 'photo_url', 'courses', 'option_groups',
            'max_quantity', 'min_quantity', 'is_promo', 'preset_discounts'])
        }).concat(addInCourseItem)
      let memberId, memberName, memberMobile
      console.log('Initial memberMobile variable')
      if (rootGetters['member/isMember']) {
        console.log('1')
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        memberName = user.name
        if (/^$|^[6|8|9|3].{7}$/.test(user.mobile)) {
          memberMobile = '+65 ' + user.mobile
        } else {
          memberMobile = '+' + user.country_code + ' ' + user.mobile
        }
        console.log('rootGetters[\'member/isMember\']', memberMobile)
        // if order for someone else
        if (modeData.isDelivery && rootGetters['delivery/address'].contact_name && rootGetters['delivery/address'].contact_name !== '') {
          memberName = rootGetters['delivery/address'].contact_name
        }
        if (modeData.isDelivery && rootGetters['delivery/address'].contact_no && rootGetters['delivery/address'].contact_no !== '') {
          // memberMobile = '+' + user.country_code + ' ' + rootGetters['delivery/address'].contact_no
          memberMobile = '+65 ' + rootGetters['delivery/address'].contact_no
          console.log('modeData.isDelivery && rootGetters[\'delivery/address\'].contact_no && rootGetters[\'delivery/address\'].contact_no !== \'\'', memberMobile)
        }
        console.log('rootGetters[\'member/isMember\']')
      } else if (modeData.isTakeaway && rootGetters['takeaway/has_guest_contact']) {
        const guestContact = rootGetters['takeaway/guest_contact']
        memberId = rootGetters['config/rid'] === 941 ? rootGetters['config/guestId'] : 0
        memberName = guestContact.name
        memberMobile = guestContact.phone
        console.log(guestContact)
        console.log('rootGetters[\'member/isMember\'] else if (rootGetters[\'takeaway/has_guest_contact\']', memberMobile)
      } else {
        console.log('modeData.isTakeaway rootGetters[\'takeaway/has_guest_contact\']')
        console.log(modeData.isTakeaway, rootGetters['takeaway/has_guest_contact'])
        console.log('modeData.isTakeaway rootGetters[\'takeaway/has_guest_contact\']')
        memberId = rootGetters['config/rid'] === 941 ? rootGetters['config/guestId'] : 0 // rootGetters['config/guestId']
        memberName = rootGetters['config/guestName']
        memberMobile = ''
        console.log('rootGetters[\'member/isMember\'] else ', memberMobile)
      }
      let delivery, takeAwayAt, tableName, totTimeRequired
      if (modeData.isTakeaway) {
        delivery = null
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableName = taTable ? taTable.qr_code : ''
        takeAwayAt = timeData.data
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else if (modeData.isDelivery) {
        const deliveryPlu = rootGetters['delivery/deliveryInfo'].delivery_plu
        if (deliveryPlu) {
          items.push({
            category_name: deliveryPlu.category_name,
            category_alternate_name: deliveryPlu.category_alternate_name,
            name: deliveryPlu.item_name,
            alternativeName: deliveryPlu.item_alternate_name,
            portion_size_name: deliveryPlu.portion_size_name,
            priceText: '',
            price: deliveryPlu.plu_price,
            priceow: deliveryPlu.plu_price,
            time_required: 0,
            tray_count: '0.00',
            modifiers: [],
            hiddenModifers: [],
            qty: 1,
            uniqueID: deliveryPlu.item_id,
            PresetDiscountID: 0,
            pluID: deliveryPlu.plu_id,
            setMealID: 0,
            rmks: '',
            non_discount: deliveryPlu.non_discount,
            courseLevel: 0
          })
        }
        delivery = rootGetters['delivery/address']
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableName = taTable ? taTable.qr_code : ''
        takeAwayAt = timeData.data
        totTimeRequired = rootGetters['cart/cartTimeRequired']
        memberMobile += ' [' + rootGetters['delivery/addressString'] + ']'
        console.log(' else if (modeData.isDelivery) ', memberMobile)
      } else {
        delivery = null
        if (!rootGetters['config/withTableNo']) {
          const table = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 0) : null
          tableName = table ? table.qr_code : ''
        } else {
          tableName = rootGetters['config/tb']
        }
        takeAwayAt = null
        totTimeRequired = 0
      }
      if (rootGetters['cart/discountInfoIsFilled'] || modeData.hasPresetDiscount) {
        const cartDiscountInfo = rootGetters['cart/discountInfo']
        const discountInfo = parseFloat(cartDiscountInfo.discount_amount || 0) > parseFloat(modeData.presetDiscount.discount_amount || 0)
        ? cartDiscountInfo
        : modeData.presetDiscount
        items.push({
          price: 0,
          pluID: -1,
          name: discountInfo.discount_name,
          PresetDiscountID: discountInfo.discount_id
        })
      }
      const params = {
        memberid: memberId,
        items: items,
        cover: 1,
        time: currentTimeData.data,
        tableName: tableName
      }
      let transid
      if (!state.transid) {
        commit(types.SET_TRANSID, rootGetters['config/browserid'])
      }
      transid = state.transid
      console.log('Json phone_number', memberMobile)
      const json = {
        params: params,
        transid: transid,
        terminalID: memberName,
        salesType: rootGetters['config/restaurant'].sales_type,
        tot_time_required: totTimeRequired,
        delivery: delivery,
        user: memberName,
        phone_number: memberMobile
      }

      if (state.paymentMode && state.paymentMode.code === 'pay_at_counter') {
        json.hold_order = 1
      }

      const request = {
        restaurant_id: parseInt(rootGetters['config/rid']),
        member_id: memberId,
        order_date: currentTimeData.data,
        json_content: json,
        allow_guest: rootGetters['config/allowCheckout'].guest ? 1 : 0,
        takeaway_at: takeAwayAt,
        amount: state.activeOrder && state.activeOrder.payments_topay ? state.activeOrder.payments_topay : null,
        dine_mode: modeData.dineMode
      }
      const { data } = await axios.post('/api/submit', request, {
        timeout: 180000
      })
      if (data.code === 1) {
        const payload = { ...state.activeOrder, reference_id: data.reference_id, items: rootGetters['cart/cart'] }
        commit(types.CHECK_SUBMIT_ORDER, payload)
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'checkout')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'checkout')
    }
  },

  // First mobile_order (with status 1) - For Jade POS
  async checkSubmitOrder2 ({ commit, state, rootState, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      const modeData = rootState.config.mode
      const plus = rootGetters['cart/cart'].map(p => {
        const item = {
          id: p.plu_id,
          quantity: Math.abs(p.quantity),
          remarks: p.remarks,
          selectedCourseItems: buildCourseItem(p.courses)
        }

        const presetDiscount = presetDiscountMixin.methods.calculateAvailablePresetDiscount(item, rootGetters['auth/user'], item.quantity)
        if (presetDiscount) {
          item.PresetDiscountID = presetDiscount.ID
        }

        if (p.price_override) item.price_overwrite = p.price_override
        return item
      })
      let takeAway, delivery, takeAwayAt, tableId, tableName, totTimeRequired
      if (modeData.isTakeaway) {
        takeAway = 1
        delivery = null
        takeAwayAt = timeData.data
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableId = taTable ? taTable.ID : 0
        tableName = taTable ? taTable.Name : ''
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else if (modeData.isDelivery) {
        const deliveryPlu = rootGetters['delivery/deliveryInfo'].delivery_plu
        if (deliveryPlu) {
          plus.push({
            id: deliveryPlu.plu_id,
            quantity: 1,
            price_overwrite: deliveryPlu.plu_price,
            remarks: '',
            name: deliveryPlu.item_name
          })
        }
        takeAway = 1
        delivery = rootGetters['delivery/address']
        takeAwayAt = timeData.data
        const taTable = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 1) : null
        tableId = taTable ? taTable.ID : 0
        tableName = taTable ? taTable.Name : ''
        totTimeRequired = rootGetters['cart/cartTimeRequired']
      } else {
        takeAway = 0
        delivery = null
        takeAwayAt = null
        if (!rootGetters['config/withTableNo']) {
          const table = rootGetters['config/tables'] ? rootGetters['config/tables'].find(t => parseInt(t.IsTakeaway) === 0) : null
          tableId = table ? table.ID : ''
          tableName = table ? table.Name : ''
        } else {
          tableId = rootGetters['config/tbdata'].ID
          tableName = rootGetters['config/tbdata'].Name
        }
        totTimeRequired = 0
      }
      // Select whether to apply cart discount, or dine mode's preset discount
      let discountId = 0
      if (rootGetters['cart/discountInfoIsFilled'] || modeData.hasPresetDiscount) {
        const cartDiscountInfo = rootGetters['cart/discountInfo']
        discountId = parseFloat(cartDiscountInfo.discount_amount || 0) > parseFloat(modeData.presetDiscount.discount_amount || 0)
        ? parseInt(cartDiscountInfo.discount_id)
        : parseInt(modeData.presetDiscount.discount_id)
      }
      const params = {
        group_id: parseInt(rootGetters['config/gid']),
        restaurant_id: parseInt(rootGetters['config/rid']),
        member_id: rootGetters['member/isMember'] ? rootGetters['member/profile'].loyalty_member_id : 0,
        takeaway: takeAway,
        delivery: delivery,
        takeaway_at: takeAwayAt,
        table: {
          id: tableId,
          name: tableName
        },
        plus: plus,
        discount_id: discountId,
        vouchers: rootGetters['cart/vouchers'],
        tot_time_required: totTimeRequired,
        amount: state.activeOrder && state.activeOrder.payments_topay ? state.activeOrder.payments_topay : null
      }
      const { data } = await axios.post('/api/submit2', params)
      if (data.code === 1) {
        const payload = { ...state.activeOrder, reference_id: data.reference_id, queue_no: data.transaction_id, items: rootGetters['cart/cart'] }
        commit(types.CHECK_SUBMIT_ORDER, payload)
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'checkout')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'checkout')
    }
  },

  async fetchActiveQueue ({ commit, state }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/queue', {
        reference_type: state.activeOrder.reference_type,
        reference_id: state.activeOrder.reference_id,
        is_guest: 0,
        amount: state.activeOrder.payments_topay
      })
      if (data.code === 1) {
        sessionStorage.setItem('api/queue','1') // gzf 2022-8-2
        commit(types.SAVE_QUEUE, { ...data.order, order_type: 'order' })
        commit(types.UPDATE_ACTIVE_ORDER, data.order)
        commit(types.FETCH_ORDERS, {
          orders: state.orders,
          time: data.time
        })
      } else if (data.msg) {
        sessionStorage.setItem('api/queue',data.msg) // gzf 2022-8-2
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'queue')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'queue')
    }
  },

  async fetchQueue ({ commit, state }, payload) {
    // payload is reference_id
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/queue', payload)

      if (data.code === 1) {
        commit(types.SAVE_QUEUE, { ...data.order, order_type: 'order' })
        commit(types.FETCH_ORDERS, {
          orders: state.orders,
          time: data.time
        })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'queue')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'queue')
    }
  },

  async updateOrderQueues ({ commit, dispatch, state, rootGetters }) {
    const orderSet = state.orders.filter(o => o.order_type === 'order' && !o.queue_no)
    if (orderSet.length > 0) {
      orderSet.map(async (order) => {
        await dispatch('fetchQueue', {
          reference_id: order.reference_id,
          is_guest: rootGetters['member/isMember'] ? 0 : 1,
          amount: order.payments_topay
        })
      })
      const { data } = await axios.get('/api/date/queue')
      commit(types.FETCH_ORDERS, {
        orders: state.orders,
        time: data
      })
    }
  },

  async updateQueues ({ commit, state }) {
    const { data } = await axios.get('/api/date/queue')
    commit(types.FETCH_ORDERS, {
      orders: state.orders,
      time: data
    })
  },

  async fetchOrderPoints ({ commit, rootGetters }, payload) {
    // payload is { reference_id, transaction_id }
    commit(types.RESET_ORDER_MSG)
    const rid = parseInt(rootGetters['config/rid']) === 0 ? parseInt(rootGetters['config/rid2']) : parseInt(rootGetters['config/rid'])
    try {
      const params = {
        member_id: rootGetters['member/profile'].loyalty_member_id,
        restaurant_id: rid,
        reference_id: payload.reference_id,
        transaction_id: payload.transaction_id
      }
      const { data } = await axios.post('/api/order/points', params)
      if (data.code === 1) {
        commit(types.SAVE_POINTS, { ...data.order, order_type: 'order' })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'points')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'points')
    }
  },

  resetQueues ({ commit }) {
    commit(types.RESET_QUEUES)
  },

  resetOrder ({ commit }) {
    commit(types.RESET_ORDER)
  },

  cancelOrder ({ commit }, payload) {
    commit(types.CANCEL_ORDER, payload)
  },

  setPaymentMode ({ commit }, payload) {
    commit(types.SET_PAYMENT_MODE, payload)
  },

  resetPaymentMode ({ commit }) {
    commit(types.RESET_PAYMENT_MODE)
  },

  enableUsePoints ({ commit }) {
    commit(types.ENABLE_USE_POINTS)
  },

  disableUsePoints ({ commit }) {
    commit(types.DISABLE_USE_POINTS)
  },

  enableUseVouchers ({ commit }) {
    commit(types.ENABLE_USE_VOUCHERS)
  },

  disableUseVouchers ({ commit }) {
    commit(types.DISABLE_USE_VOUCHERS)
  },

  resetTransid ({ commit }) {
    commit(types.RESET_TRANSID)
  },

  resetBill ({ commit }) {
    commit(types.RESET_BILL)
  },

  async fetchMemberOpenTables ({ commit, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/mobileorder/opentables', {
        restaurant_id: parseInt(rootGetters['config/rid']) === 0 ? parseInt(rootGetters['config/rid2']) : parseInt(rootGetters['config/rid']),
        member_id: rootGetters['member/profile'].loyalty_member_id,
        duration: 3
      })
      commit(types.SET_OPEN_TABLES, data)
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'bills')
    }
  },

  async previewBill ({ commit, rootGetters }, payload) {
    commit(types.RESET_ORDER_MSG)
    const rid = parseInt(rootGetters['config/rid']) === 0 ? parseInt(rootGetters['config/rid2']) : parseInt(rootGetters['config/rid'])
    try {
      const { data } = await axios.post('/api/mobileorder/previewbill', {
        restaurant_id: rid,
        member_id: rootGetters['member/profile'].loyalty_member_id,
        table_id: payload
      })
      if (data) {
        commit(types.SET_BILL, data)
      } else {
        commit(types.SET_ORDER_MSG, 'bill')
        commit(types.RESET_BILL)
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'bill')
      commit(types.RESET_BILL)
    }
  },

  async updateMobileOrderStatuses ({ commit, dispatch, state }) {
    const orderSet = state.orders.filter(o => o.order_type === 'unpaid_order' && (!o.pos_status || o.pos_status === 'pending'))
    if (orderSet.length > 0) {
      orderSet.map(async (order) => {
        await dispatch('fetchMobileOrderStatus', order.reference_id)
      })
    }
    const { data } = await axios.get('/api/date/queue')
    commit(types.FETCH_ORDERS, {
      orders: state.orders,
      time: data
    })
  },

  async fetchActiveMobileOrderStatus ({ commit, state, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const memberId = rootGetters['member/isMember'] ? rootGetters['member/profile'].loyalty_member_id : 0
      const { data } = await axios.post('/api/mobileorder/status', {
        reference_id: state.activeOrder.reference_id,
        member_id: memberId
      })
      // Update confirmed
      if (data.code === 1) {
        commit(types.CONFIRM_ORDER, { ...data.order, order_type: 'unpaid_order' })
        commit(types.UPDATE_ACTIVE_ORDER, data.order)
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'order')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order')
    }
  },

  async fetchMobileOrderStatus ({ commit, rootGetters }, payload) {
    // payload is reference_id
    commit(types.RESET_ORDER_MSG)
    try {
      const memberId = rootGetters['member/isMember'] ? rootGetters['member/profile'].loyalty_member_id : 0
      const { data } = await axios.post('/api/mobileorder/status', {
        reference_id: payload,
        member_id: memberId
      })
      // Update confirmed
      if (data.code === 1) {
        commit(types.CONFIRM_ORDER, { ...data.order, order_type: 'unpaid_order' })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'order')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order')
    }
  },

  async resendMobileOrder ({ commit }, payload) {
    // payload is reference_id
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/mobileorder/resend', {
        reference_id: payload,
        member_id: memberId
      })
      // Update confirmed
      if (data.code === 1) {
        commit(types.CONFIRM_ORDER, { ...data.order, order_type: 'unpaid_order' })
      } else if (data.msg) {
        commit(types.SET_ORDER_MSG, data.msg)
      } else {
        commit(types.SET_ORDER_MSG, 'order')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order')
    }
  },

  async sendMail ({ commit, rootGetters }, payload) {
    commit(types.RESET_ORDER_MSG)
    try {
      let recipientName, recipientEmail, recipientContact
      let countryCode, phoneNumber, receiver, receiverContact
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        recipientName = user.name
        recipientEmail = user.email

        let countryCode = user.country_code

        if (/^$|^[6|8|9|3].{7}$/.test(user.mobile)) {
          countryCode = '65'
        }

        recipientContact = '+ ' + countryCode + ' ' + user.mobile

        receiver = (rootGetters['member/isMember']) ? rootGetters['delivery/address'].contact_name : ''
        receiverContact = (rootGetters['member/isMember']) ? rootGetters['delivery/address'].contact_no : ''
      } else {
        // recipientEmail = 'erica.yap@aptsys.com.sg'
        // recipientContact = '+ 65 88888888'
        const guest = rootGetters['cart/guestDetails']
        recipientName = rootGetters['config/guestName']
        recipientEmail = guest.email ? guest.email : 'junye.teo@aptsys.com.sg'
        countryCode = guest.country_code ? guest.country_code : '65'
        phoneNumber = guest.mobile ? guest.mobile : '88888888'
        receiver = rootGetters['config/guestName']
        recipientContact = '+ ' + countryCode + ' ' + phoneNumber
        receiverContact = '+ ' + countryCode + ' ' + phoneNumber
      }
      const { data } = await axios.post('/api/email/order', {
        receiptHeader: rootGetters['config/gemsSetting'].Header ? rootGetters['config/gemsSetting'].Header : '',
        storeName: rootGetters['config/config'].full_name,
        recipientName: recipientName,
        recipientEmail: recipientEmail,
        recipientContact: recipientContact,
        receiver: receiver,
        receiver_contact: receiverContact,
        receiptBcc: rootGetters['config/config'].email_order_bcc,
        remarks: rootGetters['cart/remarks'] ? rootGetters['cart/remarks'] : '',
        helpFooter: rootGetters['config/config'].email_help_footer,
        order: payload
      })
      if (data !== 1) {
        commit(types.SET_ORDER_MSG, 'email2')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'email2')
    }
  },

  async saveActiveOrderEpayClientRef ({ commit, state, rootState, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const activeOrder = state.activeOrder
      const timeData = await axios.post('/api/date/pickup', {
        pickup_date: rootGetters['cart/pickupDate'],
        pickup_time: rootGetters['cart/pickupTime']
      })
      let memberId, userId, recipientName, recipientEmail, recipientContact, receiver, receiverContact
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        userId = user.id
        recipientName = user.name
        recipientEmail = user.email

        let countryCode = user.country_code

        if (/^$|^[6|8|9|3].{7}$/.test(user.mobile)) {
          countryCode = '65'
        }

        recipientContact = '+ ' + countryCode + ' ' + user.mobile

        receiver = rootGetters['delivery/address'].contact_name
        receiverContact = rootGetters['delivery/address'].contact_no
      } else {
        const guest = rootGetters['cart/guestDetails']
        memberId = rootGetters['config/guestId']
        userId = 0
        recipientName = guest.name
        recipientEmail = guest.email
        recipientContact = '+ ' + guest.country_code + ' ' + guest.mobile
        receiver = guest.name
        receiverContact = '+ ' + guest.country_code + ' ' + guest.mobile
      }
      let paymentModeData = null
      if (state.paymentMode) {
        paymentModeData = state.paymentMode
      } else {
        if (rootGetters['config/paymentModes']) {
          paymentModeData = rootGetters['config/paymentModes'][0]
        }
      }
      const paymentType = paymentModeData ? paymentModeData.code : null
      const modeData = rootState.config.mode
      const config = rootGetters['config/config']
      await axios.post('/api/epay/client_ref/add', {
        member_id: memberId,
        reference_id: parseInt(activeOrder.reference_id),
        restaurant_id: parseInt(rootGetters['config/rid']),
        reference_type: 'activeOrder',
        content: {
          ...activeOrder,
          user_id: userId,
          member_id: memberId,
          reference_type: rootGetters['config/posType'],
          restaurant_id: parseInt(rootGetters['config/rid']),
          location: rootGetters['config/config'].location,
          payment_type: paymentType,
          pickup_time: (modeData.isTakeaway || modeData.isDelivery) ? timeData.data : null,
          tablename: modeData.isDinein && rootGetters['config/withTableNo'] ? rootGetters['config/tbdata'].Name : null,
          address: modeData.isDelivery ? rootGetters['delivery/addressString'] : null,
          is_refunded: 0
        },
        email_content: {
          receiptHeader: rootGetters['config/gemsSetting'].Header ? rootGetters['config/gemsSetting'].Header : '',
          storeName: config.full_name,
          recipientName: recipientName,
          recipientEmail: recipientEmail,
          recipientContact: recipientContact,
          receiver: receiver,
          receiver_contact: receiverContact,
          receiptBcc: rootGetters['config/config'].email_order_bcc,
          remarks: rootGetters['cart/remarks'] ? rootGetters['cart/remarks'] : '',
          helpFooter: config.email_help_footer
        }
      })
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order2')
    }
  },

  async saveActiveBillEpayClientRef ({ commit, state, rootGetters }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const activeBill = state.activeBill
      const openTable = rootGetters['order/openTables'].find(i => parseInt(i.table_id) === parseInt(activeBill.tid))
      let memberId, userId, recipientName, recipientEmail, recipientContact, receiver, receiverContact
      if (rootGetters['member/isMember']) {
        const user = rootGetters['auth/user']
        const profile = rootGetters['member/profile']
        memberId = profile.loyalty_member_id
        userId = user.id
        recipientName = user.name
        recipientEmail = user.email

        let countryCode = user.country_code

        if (/^$|^[6|8|9|3].{7}$/.test(user.mobile)) {
          countryCode = '65'
        }

        recipientContact = '+ ' + countryCode + ' ' + user.mobile

        receiver = rootGetters['delivery/address'].contact_name
        receiverContact = rootGetters['delivery/address'].contact_no
      } else {
        const guest = rootGetters['cart/guestDetails']
        memberId = rootGetters['config/guestId']
        userId = 0
        recipientName = guest.name
        recipientEmail = guest.email
        recipientContact = '+ ' + guest.country_code + ' ' + guest.mobile
        receiver = guest.name
        receiverContact = '+ ' + guest.country_code + ' ' + guest.mobile
      }
      const config = rootGetters['config/config']
      await axios.post('/api/epay/client_ref/add', {
        member_id: memberId,
        reference_id: parseInt(activeBill.preview_bill_id),
        restaurant_id: parseInt(rootGetters['config/rid']),
        reference_type: 'activeBill',
        content: {
          ...activeBill,
          user_id: userId,
          member_id: memberId,
          reference_id: parseInt(activeBill.preview_bill_id),
          restaurant_id: parseInt(rootGetters['config/rid']),
          tablename: openTable && openTable.m_table ? openTable.m_table.Name : null,
          open_date: activeBill.opendate,
          items: activeBill.order,
          sub_total: activeBill.subtotal,
          gst_name: activeBill.gstname,
          gst_total: activeBill.gst,
          svc_name: activeBill.svcname,
          svc_total: activeBill.svc
        },
        email_content: {
          receiptHeader: rootGetters['config/gemsSetting'].Header ? rootGetters['config/gemsSetting'].Header : '',
          storeName: config.full_name,
          recipientName: recipientName,
          recipientEmail: recipientEmail,
          recipientContact: recipientContact,
          receiver: receiver,
          receiver_contact: receiverContact,
          receiptBcc: rootGetters['config/config'].email_order_bcc,
          remarks: rootGetters['cart/remarks'] ? rootGetters['cart/remarks'] : '',
          helpFooter: config.email_help_footer
        }
      })
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'bill2')
    }
  },

  // Insert into local orders table, activeOrder is from EpayClientRefs table
  async addOrderByClientRef ({ commit, state }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/orders/add', {
        order: state.activeOrder
      })
      if (data) {
        sessionStorage.setItem('api/orders/add', '1') // gzf 2022-8-2
        const order2 = data
        order2.order_type = 'order'
        order2.trays = order2.trays ? JSON.parse(order2.trays) : []
        order2.items = JSON.parse(order2.items)
        if (order2.vouchers) {
          order2.vouchers = JSON.parse(order2.vouchers)
        }
        commit(types.UPDATE_ACTIVE_ORDER, order2)
        commit(types.ADD_ORDER, order2)
      } else {
        commit(types.SET_ORDER_MSG, 'order2')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order2')
    }
  },

  // Insert into local receipts table, activeBill is from EpayClientRefs table
  async addReceiptByClientRef ({ commit, state, dispatch }) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/receipts/add', {
        bill: state.activeBill
      })
      if (data) {
        const receipt = data
        receipt.items = receipt.items ? JSON.parse(receipt.items) : []
        commit(types.UPDATE_ACTIVE_BILL, receipt)
        dispatch('receipt/addReceipt', receipt, { root: true })
      } else {
        commit(types.SET_ORDER_MSG, 'bill2')
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'bill2')
    }
  },

  async fetchActiveOrderEpayClientRef ({ commit }, payload) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/epay/client_ref/get', {
        reference_id: payload,
        reference_type: 'activeOrder'
      })
      if (data.content) {
        sessionStorage.setItem('api/epay/client_ref/get','1') // gzf 2022-8-2
        commit(types.UPDATE_ACTIVE_ORDER, JSON.parse(data.content))
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'order')
    }
  },

  async fetchActiveBillEpayClientRef ({ commit }, payload) {
    commit(types.RESET_ORDER_MSG)
    try {
      const { data } = await axios.post('/api/epay/client_ref/get', {
        reference_id: payload,
        reference_type: 'activeBill'
      })
      if (data.content) {
        commit(types.UPDATE_ACTIVE_BILL, JSON.parse(data.content))
      }
    } catch (e) {
      commit(types.SET_ORDER_MSG, 'bill')
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/order.js